import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Circle,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
  VStack,
  Spacer
} from "@chakra-ui/react";
import { Auth, cqAPI } from "../../lib/Auth";
import EditableText from "../EditableText/EditableText";
import moment from "moment";
import {
  CAN_START_SYNC,
  PMS_PASSWORD_NOT_REQUIRED,
  WORKER_CURVEDENTAL,
  WORKER_DENTICON,
  WORKER_DENTRIX,
  WORKER_EAGLESOFT,
  WORKER_KOLLA,
  WORKER_OPENDENTAL,
  WORKER_ORTHOTRAC,
  WORKER_PRACTICEWORKS,
  WORKER_QSI,
  WORKER_SOFTDENT,
  WORKER_WINOMS,
  KOLLA_CONNECTORS,
  WORKER_CLOUD9,
  WORKER_CARESTACK,
  WORKER_DENTRIXASCEND,
  canSelfServe
} from "./constants";
import {
  ConnectorButton,
  useKollaSDK
} from "@kolla/react-sdk";

const ClientTokenSettings = ({
  business, 
  config, 
  supportedPMS, 
  fetchClientConfig, 
  pmsSyncJob, 
  lastDataReceived,
  kollaConsumertoken,
  requestKollaConsumerToken,
  pmsStatus,
}) => {
  const isSelfServe = canSelfServe(config?.worker_type, config?.pms_type)
  const [supportedPMSVersions, setSupportedPMSVersions] = useState([]);
  const [pmsName, setPmsName] = useState("");
  const bgColor = useColorModeValue("gray.100", "gray.700");
  const toast = useToast();
  const [isKollaSelfServeEnabled, setIsKollaSelfServedEnabled] = useState(false);
  const kollaSDK = useKollaSDK();
  const lastSyncDate = pmsStatus?.last_sync ? moment(pmsStatus?.last_sync).format("LLL") : null;
  const lastSyncHealthStatus = pmsStatus?.status || 'unknown';
  const syncIssues = pmsStatus?.issues || [];
  const syncHealthColors = {
    healthy: "#4B9560",
    impaired: "#FF9641",
    failed: "#D64545",
    unknown: "#9CA3AF",
  };
  const syncHealthColor = syncHealthColors[lastSyncHealthStatus] || syncHealthColors.unknown;
  const syncHealthLabels = {
    healthy: "Healthy — data is syncing normally.",
    impaired: "Impaired — recovering from issues detected within the last 60 minutes.",
    failed: "Failed — sync is not working. Please contact support.",
    unknown: "Unknown — sync status is not yet available.",
  };
  // The most recent moment an issue finished (issues[].completed). Empty when healthy.
  const latestIssueCompleted = syncIssues
    .map((issue) => issue?.completed)
    .filter(Boolean)
    .sort()
    .pop();
  const syncHealthLabel = `${syncHealthLabels[lastSyncHealthStatus] || syncHealthLabels.unknown}${
    latestIssueCompleted
      ? ` Last issue detected on ${moment(latestIssueCompleted).format("LLL")}.`
      : ""
  }`;

  useEffect(() => {
    const [name, entry] = Object.entries(supportedPMS).find(
      ([, entry]) => entry.worker_type === config.worker_type && entry.pms_type === config.pms_type
    );
    setPmsName(name);
    setSupportedPMSVersions(entry?.versions || []);
  }, []);
  
  useEffect(() => {
    if (!isSelfServe) {
      return;
    }
    
    if (pmsSyncJob && config?.kolla?.connector_id && config?.kolla?.consumer_id) {
      setIsKollaSelfServedEnabled(false);
      return;
    }
    
    requestKollaConsumerToken().catch(
      err => console.error("Cannot fetch kolla consumer token:", err)
    );
   },
   [config?.worker_type, config?.pms_type, pmsSyncJob]  
 )
  
  const updateClientField = async (field, value) => {
    await cqAPI.post("/settings/client", {
      worker_type: config.worker_type,
      pms_type: config.pms_type,
      [field]: value
    }).then(async () => await fetchClientConfig());
    return true;
  };
  
  const updateMultipleClientFields = async (fields = {}) => {
    await cqAPI.post("/settings/client", {
      worker_type: config.worker_type,
      pms_type: config.pms_type,
      ...fields
    }).then(async () => await fetchClientConfig());
    return true;
  };

  useEffect(() => {
      const checkKollaConnector = async () => {
        if (!kollaConsumertoken) {
          setIsKollaSelfServedEnabled(false);
          return;
        }
        
        if (pmsSyncJob && config?.kolla?.connector_id && config?.kolla?.consumer_id) {
          setIsKollaSelfServedEnabled(false);
          return;
        }
        
        const connector_id = KOLLA_CONNECTORS[config?.pms_type];
        kollaSDK.consumerToken = kollaConsumertoken;
        const result = await kollaSDK.getConnectorLinkedAccounts(connector_id);
        console.log("Connector", result);
        console.log("Config", config);
        
        if (!result?.authenticated) {
          return;
        }
        
        if (result?.linked_accounts[connector_id]?.status !== 'ACTIVE') {
          setIsKollaSelfServedEnabled(true);
          return;
        } else {
          setIsKollaSelfServedEnabled(false);
        }
      };
      
      checkKollaConnector();
    },
    [kollaConsumertoken]
  );
      
  kollaSDK.subscribe("onError",
    (eventData) => {
      console.log('onError', eventData);
    }
  );
  
  kollaSDK.subscribe("onLinkCreated",
    (eventData) => {
      console.log('onLinkCreated', eventData);
      
      const connector_id = eventData?.connector?.connector_id;
      const consumer_id = eventData?.user?.consumerId;
      
      if (connector_id && consumer_id) {
        if (config?.kolla?.connector_id !== connector_id || config?.kolla?.consumer_id !== consumer_id) {
          updateMultipleClientFields({
            "kolla.connector_id": connector_id,
            "kolla.consumer_id": consumer_id
          }).catch(err => console.error("Cannot update Kolla connector", err));
        }
        
        setIsKollaSelfServedEnabled(false);
      }
    }
  );
  
  kollaSDK.subscribe("onMarketplaceClosed",
    (eventData) => {
      console.log('onMarketplaceClosed', eventData);
      
      const connector_id = eventData?.connector?.connector_id;
      const consumer_id = eventData?.user?.consumerId;
      
      kollaSDK.getConnectorLinkedAccounts(connector_id).then(
        result => {
          if (!result?.authenticated) {
            return;
          }
          
          updateMultipleClientFields({
            "kolla.connector_id": connector_id,
            "kolla.consumer_id": consumer_id
          }).then(
            () => setIsKollaSelfServedEnabled(false)
          ).catch(err => console.error("Cannot update Kolla connector", err));
        }
      ).catch(err => console.error("Cannot retrieve linked Kolla accounts", err));
    }
  );
 
  const allowed_capacity = {
    100: "Minimal range import every 1 hour. Batch 200.",
    75: "Minimal range import every 1.5 hours. Batch 150.",
    50: "Minimal range import every 2 hours. Batch 100.",
    25: "Minimal range import every 4 hours. Batch 50.",
  };

  const cloudSync = async (action) => {
    let response;
    switch (action) {
      case "start":
        response = await cqAPI.post("/client/startSync");
        break;
      case "stop":
        response = await cqAPI.post("/client/stopSync");
        break;
    }
    if (response.type === 'error' && response.msg) {
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } else {
      await fetchClientConfig();
    }
  };

  const getPMSUpdateFreq = (name) => {
    switch (name) {
      case WORKER_KOLLA:
        return ['practices', 'patients', 'appointments', 'treatments'];
      case WORKER_CLOUD9:
        return ['practices', 'patients', 'appointments', 'treatments'];
      case WORKER_CARESTACK:
        return ['practices', 'patients', 'appointments', 'treatments'];
      case WORKER_DENTRIXASCEND:
        return ['practices', 'patients', 'appointments', 'treatments', 'ledger', 'documents'];
    }

    return Object.keys(config?.updateFreq);
  };

  // pmsSyncJob shape from /client/getSettings:
  //   null                                  → never started
  //   { paused: true,  running, nextAt }    → paused via End Sync
  //   { paused: false, running, nextAt }    → active
  const renderSyncControl = () => {
    if (!pmsSyncJob) {
      return (
        <Button colorScheme="green" ml={4} onClick={() => cloudSync('start')}>
          Start Sync
        </Button>
      );
    }
    if (pmsSyncJob.paused) {
      return (
        <Button colorScheme="green" ml={4} onClick={() => cloudSync('start')}>
          Restart Sync
        </Button>
      );
    }
    return (
      <Flex alignItems="center" justifyContent="flex-end">
        {Auth.user?.isSuperAdmin && <Text fontSize="sm" mr={2}>
          {pmsSyncJob.running
            ? 'Syncing…'
            : pmsSyncJob.nextAt
              ? `Next: ${moment(pmsSyncJob.nextAt).format('LT')}`
              : 'Active'}
        </Text>}
        <Button colorScheme="red" ml={4} onClick={() => cloudSync('stop')}>
          End Sync
        </Button>
      </Flex>
    );
  };

  return (
      <VStack spacing={4} align="stretch">
        <Flex mt={4} justify="space-between" wrap="wrap">
          <div style={{ display: "block", width: "100%" }}>
            {CAN_START_SYNC.includes(config?.worker_type) && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Flex>
                  <Text fontSize="xl" paddingRight="10px" textAlign="left">
                    {pmsName} update settings
                  </Text>
                  <Spacer />
                  {CAN_START_SYNC.indexOf(config?.worker_type) !== -1 &&
                    (!business?.parent || business?.has_children || config?.worker_type === WORKER_KOLLA) && (
                      <Box textAlign="right" borderRadius="md" mb={4} mr={2}>
                        {renderSyncControl()}
                      </Box>
                    )}
                </Flex>
                {getPMSUpdateFreq(config?.worker_type).map((key) => (
                  <Flex flexGrow={1} key={key} alignItems="center" paddingTop="10px">
                    <Text paddingRight="10px" textAlign="left">
                      {key === "fullDownload"
                        ? "Full download (in hours)"
                        : `${key} (in minutes)`}:
                    </Text>
                    <EditableText
                      pattern={key === "fullDownload" ? /^(0|\d{1,})$/ : /^(0|\d{1,})$/}
                      invalid="Cannot be empty. This is a numerical value that must be greater than 15."
                      initialValue={Math.max(config?.updateFreq[key], 15).toString()}
                      onSave={async (newText) => {
                        const newValue = parseInt(newText);
                        if (newValue < 15) {
                          return false;
                        }
                        return updateClientField(`updateFreq.${key}`, newText);
                      }}
                      onCancel={async () => { }}
                      placeholder=""
                      isNameField={false}
                    />
                  </Flex>
                ))}
              </Box>
            )}
            {!CAN_START_SYNC.includes(config?.worker_type) &&
              !PMS_PASSWORD_NOT_REQUIRED.includes(config?.worker_type) &&
              config?.worker_type && (
                <Box
                  p={4}
                  bgColor={bgColor}
                  borderRadius="md"
                  width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                  mb={4}
                  mr={{ base: 0, md: 2 }} // Responsive margin
                >
                  <Text fontSize="xl" paddingRight="10px" textAlign="left">
                    {pmsName} update settings
                  </Text>
                  <FormControl>
                    <FormLabel>PMS Version</FormLabel>
                    <Select
                      value={config.pms_version}
                      onChange={(e) =>
                        updateClientField("pms_version", e.target.value)
                      }
                    >
                      {/* Dynamically generate version options based on pmsOptions */}
                      {supportedPMSVersions.map((version, index) => (
                        <option key={index} value={version}>
                          {version}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.pms_user}
                      onSave={async (newText) => {
                        return updateClientField("pms_user", newText);
                      }}
                      pattern={/.+/}
                      invalid="Cannot be empty."
                      onCancel={async () => { }}
                      placeholder="PMS User"
                      isNameField={false}
                    />
                  </Flex>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.pms_password}
                      onSave={async (newText) => {
                        return updateClientField("pms_password", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="PMS Password"
                      isNameField={false}
                    />
                  </Flex>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.synchronization_time}
                      onSave={async (newText) => {
                        return updateClientField("synchronization_time", newText);
                      }}
                      pattern={/^\\d*$/}
                      invalid="Only number"
                      onCancel={async () => { }}
                      placeholder="Update Frequency in Minutes"
                      isNameField={false}
                    />
                  </Flex>

                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <Checkbox
                      isChecked={config?.skip_rp_for_adults}
                      onChange={(e) =>
                        updateClientField("skip_rp_for_adults", e.target.checked)
                      }
                    >
                      Skip Responsible Party for Adults
                    </Checkbox>
                  </Flex>

                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <Select
                      value={config?.capacity}
                      onChange={(e) => updateClientField("capacity", e.target.value)}
                    >
                      {/* Dynamically generate version options based on pmsOptions */}
                      {Object.keys(allowed_capacity).map((key) => (
                        <option key={key} value={key}>
                          {allowed_capacity[key]}
                        </option>
                      ))}
                    </Select>
                  </Flex>
                </Box>
              )}
            {config?.pms_type === WORKER_DENTRIX && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.db_path}
                    onSave={async (newText) => {
                      return updateClientField("db_path", newText);
                    }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    onCancel={async () => { }}
                    placeholder="DB Path"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.dentrix_auth_string}
                    onSave={async (newText) => {
                      return updateClientField("dentrix_auth_string", newText);
                    }}
                    onCancel={async () => { }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    placeholder="Auth string"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <Text paddingRight="10px" textAlign="left">
                    Code to use to to mark confirmed appointments:
                  </Text>
                  <EditableText
                    initialValue={config?.confirm_code}
                    onSave={async (newText) => {
                      return updateClientField("confirm_code", newText);
                    }}
                    onCancel={async () => { }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    placeholder="Not set"
                    isNameField={false}
                  />
                </Flex>

                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <Text paddingRight="10px" textAlign="left">
                    Statues that represent confirmed appointments:
                  </Text>
                  <EditableText
                    initialValue={config?.confirm_statuses}
                    onSave={async (newText) => {
                      return updateClientField("confirm_statuses", newText);
                    }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    onCancel={async () => { }}
                    placeholder="Not set"
                    isNameField={false}
                  />
                </Flex>

                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <Text paddingRight="10px" textAlign="left">
                    Statues for checkout:
                  </Text>
                  <EditableText
                    initialValue={config?.checkout_statuses}
                    onSave={async (newText) => {
                      return updateClientField("checkout_statuses", newText);
                    }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    onCancel={async () => { }}
                    placeholder="Not set"
                    isNameField={false}
                  />
                </Flex>
              </Box>
            )}
            {config?.pms_type === WORKER_PRACTICEWORKS && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.config_path}
                    onSave={async (newText) => {
                      return updateClientField("config_path", newText);
                    }}
                    onCancel={async () => { }}
                    placeholder="Config path"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.db_name}
                    onSave={async (newText) => {
                      return updateClientField("db_name", newText);
                    }}
                    onCancel={async () => { }}
                    placeholder="DB name"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.db_server}
                    onSave={async (newText) => {
                      return updateClientField("db_server", newText);
                    }}
                    onCancel={async () => { }}
                    placeholder="DB server"
                    isNameField={false}
                  />
                </Flex>
              </Box>
            )}
            {config?.pms_type === WORKER_SOFTDENT && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.db_path}
                    onSave={async (newText) => {
                      return updateClientField("db_path", newText);
                    }}
                    onCancel={async () => { }}
                    placeholder="DB path"
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.practice_email}
                    onSave={async (newText) => {
                      return updateClientField("practice_email", newText);
                    }}
                    onCancel={async () => { }}
                    placeholder="Practice Email"
                    isNameField={false}
                  />
                </Flex>
              </Box>
            )}
            {config?.pms_type === WORKER_ORTHOTRAC && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <Text paddingRight="10px" textAlign="left">
                    Statues for inactive patients:
                  </Text>
                  <EditableText
                    initialValue={config?.patient_inactive_statuses}
                    onSave={async (newText) => {
                      return updateClientField(
                        "config.patient_inactive_statuses",
                        newText
                      );
                    }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    onCancel={async () => { }}
                    placeholder="Not set"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.db_server}
                    onSave={async (newText) => {
                      return updateClientField("db_server", newText);
                    }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    onCancel={async () => { }}
                    placeholder="DB server"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.db_port}
                    onSave={async (newText) => {
                      return updateClientField("db_port", newText);
                    }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    onCancel={async () => { }}
                    placeholder="DB port"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.db_name}
                    onSave={async (newText) => {
                      return updateClientField("db_name", newText);
                    }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    onCancel={async () => { }}
                    placeholder="DB name"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <Checkbox
                    isChecked={config?.exclude_nullable_insurance}
                    onChange={(e) =>
                      updateClientField(
                        "config.exclude_nullable_insurance",
                        e.target.checked
                      )
                    }
                  >
                    Skip Responsible Party for Adults
                  </Checkbox>
                </Flex>
              </Box>
            )}
            {config?.pms_type === WORKER_WINOMS && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.db_name}
                    onSave={async (newText) => {
                      return updateClientField("db_name", newText);
                    }}
                    onCancel={async () => { }}
                    placeholder="DB name"
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.dsn}
                    onSave={async (newText) => {
                      return updateClientField("dns", newText);
                    }}
                    onCancel={async () => { }}
                    placeholder="DSN"
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    isNameField={false}
                  />
                </Flex>
              </Box>
            )}
            {config?.pms_type === WORKER_OPENDENTAL && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.db_name}
                    onSave={async (newText) => {
                      return updateClientField("db_name", newText);
                    }}
                    onCancel={async () => { }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    placeholder="DB name"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.dsn}
                    onSave={async (newText) => {
                      return updateClientField("dns", newText);
                    }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    onCancel={async () => { }}
                    placeholder="DSN"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.db_server}
                    onSave={async (newText) => {
                      return updateClientField("db_server", newText);
                    }}
                    onCancel={async () => { }}
                    placeholder="DB server"
                    isNameField={false}
                  />
                </Flex>
              </Box>
            )}
            {config?.pms_type === WORKER_EAGLESOFT && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.integration_key}
                    onSave={async (newText) => {
                      return updateClientField("integration_key", newText);
                    }}
                    onCancel={async () => { }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    placeholder="Integration Key"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={config?.api_server}
                    onSave={async (newText) => {
                      return updateClientField("api_server", newText);
                    }}
                    pattern={/.+/}
                    invalid="Cannot be empty."
                    onCancel={async () => { }}
                    placeholder="API Server"
                    isNameField={false}
                  />
                </Flex>
              </Box>
            )}
            {config?.pms_type === WORKER_DENTICON && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Client ID</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.denticon?.client_id}
                      onSave={async (newText) => {
                        return updateClientField("denticon.client_id", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Client ID"
                      isNameField={false}
                      pattern={
                        /^(|[A-F0-9]{8}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{12})$/
                      }
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Client Secret</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.denticon?.client_secret}
                      onSave={async (newText) => {
                        return updateClientField("denticon.client_secret", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Client Secret"
                      isNameField={false}
                      pattern={/^([a-f0-9]{32})$/}
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Subscription Key</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.denticon?.subscription_key}
                      onSave={async (newText) => {
                        return updateClientField("denticon.subscription_key", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Subscription Key (for development use only)"
                      isNameField={false}
                      pattern={/^([a-f0-9-]*)$/}
                    />
                  </Flex>
                </Flex>
              </Box>
            )}
            {config?.pms_type === WORKER_CLOUD9 && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Endpoint</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.cloud9?.endpoint}
                      onSave={async (newText) => {
                        return updateClientField("cloud9.endpoint", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Endpoint"
                      isNameField={false}
                      pattern={
                        /^https:\/\/.*/
                      }
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Client ID</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.cloud9?.client_id}
                      onSave={async (newText) => {
                        return updateClientField("cloud9.client_id", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Client ID"
                      isNameField={false}
                      pattern={
                        /^(|[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})$/
                      }
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Username</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.cloud9?.username}
                      onSave={async (newText) => {
                        return updateClientField("cloud9.username", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Username"
                      isNameField={false}
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Password</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.cloud9?.password}
                      onSave={async (newText) => {
                        return updateClientField("cloud9.password", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Password"
                      isNameField={false}
                      sensitive={true}
                    />
                  </Flex>
                </Flex>
              </Box>
            )}
            {config?.pms_type === WORKER_CARESTACK && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Base URL</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.carestack?.base_url}
                      onSave={async (newText) => {
                        return updateClientField("carestack.base_url", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Base URL"
                      isNameField={false}
                      pattern={
                        /^https:\/\/.*/
                      }
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>IDP URL</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.carestack?.idp_url}
                      onSave={async (newText) => {
                        return updateClientField("carestack.idp_url", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="IDP URL"
                      isNameField={false}
                      pattern={
                        /^https:\/\/.*/
                      }
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Client ID</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.carestack?.client_id}
                      onSave={async (newText) => {
                        return updateClientField("carestack.client_id", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Client ID"
                      isNameField={false}
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Client Secret</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.carestack?.client_secret}
                      onSave={async (newText) => {
                        return updateClientField("carestack.client_secret", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Client Secret"
                      isNameField={false}
                      pattern={
                        /^(|[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})$/
                      }
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Username</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.carestack?.username}
                      onSave={async (newText) => {
                        return updateClientField("carestack.username", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Username"
                      isNameField={false}
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Password</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.carestack?.password}
                      onSave={async (newText) => {
                        return updateClientField("carestack.password", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Password"
                      isNameField={false}
                      sensitive={true}
                    />
                  </Flex>
                </Flex>
              </Box>
            )}

            {config?.pms_type === WORKER_DENTRIXASCEND && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Organization ID</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={config?.dentrix_ascend?.organization_id}
                      onSave={async (newText) => {
                        return updateClientField("dentrix_ascend.organization_id", newText);
                      }}
                      onCancel={async () => { }}
                      placeholder="Organization ID"
                      isNameField={false}
                    />
                  </Flex>
                </Flex>
              </Box>
            )}
            
            {config?.pms_type === WORKER_QSI && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={
                      config?.qsi ? config.qsi.pgid : ""
                    }
                    onSave={async (newText) => {
                      return updateClientField("qsi.pgid", newText);
                    }}
                    pattern={/^\d+$/}
                    invalid="Cannot be empty. This is a numerical value."
                    onCancel={async () => { }}
                    placeholder="Practice Group ID (account ID)"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={
                      config?.qsi
                        ? config.qsi.username
                        : ""
                    }
                    onSave={async (newText) => {
                      return updateClientField("qsi.username", newText);
                    }}
                    pattern={/^.{5,}$/}
                    invalid="Not a valid value."
                    onCancel={async () => { }}
                    placeholder="Username"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={
                      config?.qsi && config.qsi.password
                        ? `********`
                        : ""
                    }
                    onSave={async (newText) => {
                      return updateClientField("qsi.password", newText);
                    }}
                    pattern={/^.{5,}$/}
                    invalid="Not a valid value."
                    onCancel={async () => { }}
                    placeholder="Password"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={
                      config?.qsi &&
                      config.qsi.api_organization
                    }
                    pattern={/^.{5,}$/}
                    invalid="Not a valid value."
                    onSave={async (newText) => {
                      return updateClientField("qsi.api_organization", newText);
                    }}
                    onCancel={async () => { }}
                    placeholder="Web API Organization"
                    isNameField={false}
                  />
                </Flex>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={
                      config?.qsi &&
                      config.qsi.api_password
                        ? `********`
                        : ""
                    }
                    onSave={async (newText) => {
                      return updateClientField("qsi.api_password", newText);
                    }}
                    pattern={/^.{5,}$/}
                    invalid="Not a valid value."
                    onCancel={async () => { }}
                    placeholder="Web API Password"
                    isNameField={false}
                  />
                </Flex>
              </Box>
            )}
            {config?.pms_type === WORKER_CURVEDENTAL && config?.worker_type !== WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                  <EditableText
                    initialValue={
                      config?.curve ? config.curve.url : ""
                    }
                    onSave={async (newText) => {
                      return updateClientField("curve.url", newText);
                    }}
                    pattern={/^https:\/\/[^\\.]+\\\.curvehero\\\.com\/?$/}
                    invalid="Login URL"
                    onCancel={async () => { }}
                    placeholder="Login URL"
                    isNameField={false}
                  />
                </Flex>
              </Box>
            )}
            {config?.worker_type === WORKER_KOLLA && (
              <Box
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
                mb={4}
                mr={{ base: 0, md: 2 }} // Responsive margin
              >
                <Text fontSize="xl" paddingRight="10px" textAlign="left">
                  {pmsName} settings
                </Text>
                <FormControl>
                  <FormLabel>PMS Version</FormLabel>
                  <Select
                    value={config.pms_version}
                    onChange={(e) =>
                      updateClientField("pms_version", e.target.value)
                    }
                  >
                    {/* Dynamically generate version options based on pmsOptions */}
                    {supportedPMSVersions.map((version, index) => (
                      <option key={index} value={version}>
                        {version}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Connector ID</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={
                        config?.kolla?.connector_id || ""
                      }
                      onSave={async (newText) => {
                        return updateClientField('kolla.connector_id', newText);
                      }}
                      pattern={/^.+$/}
                      invalid="Cannot be empty."
                      onCancel={async () => { }}
                      placeholder="Connector ID"
                      isNameField={false}
                    />
                  </Flex>
                </Flex>
                <Flex flexGrow={1} paddingTop="10px" direction="column">
                  <FormLabel>Consumer ID</FormLabel>
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    <EditableText
                      initialValue={
                        config?.kolla?.consumer_id || ""
                      }
                      onSave={async (newText) => {
                        return updateClientField("kolla.consumer_id", newText);
                      }}
                      pattern={/^.+$/}
                      invalid="Not a valid value."
                      onCancel={async () => { }}
                      placeholder="Consumer ID"
                      isNameField={false}
                    />
                  </Flex>
                </Flex>
                {isSelfServe && (
                  <Flex flexGrow={1} alignItems="center" paddingTop="10px">
                    {isKollaSelfServeEnabled && (
                      <ConnectorButton connectorID={KOLLA_CONNECTORS[config?.pms_type]} />
                    )}
                  </Flex>
                )}
              </Box>
            )}
          </div>
        </Flex>
        {Object.keys(lastDataReceived || {}).length && (
          <Box
            p={4}
            bgColor={bgColor}
            borderRadius="md"
            width={{ base: "100%", md: "calc(50% - 10px)" }} // Responsive width
            mb={4}
            mr={{ base: 0, md: 2 }} // Responsive margin
          >
            <Tooltip label={syncHealthLabel} hasArrow placement="top">
              <Flex alignItems="center" paddingBottom="20px" width="fit-content">
                <Text fontWeight="bold" textAlign="left">
                  Sync health:
                </Text>
                <Circle size="12px" bg={syncHealthColor} ml={2} />
              </Flex>
            </Tooltip>
            <Box mb={8} textAlign="left">
              {lastSyncDate && (
                <Text textAlign="left">
                  Last sync: {lastSyncDate}
                </Text>
              )}
              {!lastSyncDate && (
                <Text textAlign="left">
                  No sync performed
                </Text>
              )}
            </Box>
            <Text paddingBottom="20px" fontWeight="bold" textAlign="left">
              {pmsName} adapter has received PMS data for the following dates:
            </Text>
            {Object.keys(lastDataReceived || {}).map((key) => (
              <Text key={key} mb={4} textAlign="left">
                {key}: {moment(lastDataReceived[key]).format("LLL")}
              </Text>
            ))}
          </Box>
        )}
    </VStack>
  );
};

export default ClientTokenSettings;
